

import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Table, Button, Row, Col, message, Spin, Space, Tooltip, Typography,Popconfirm } from 'antd';
import { EditOutlined, TrophyFilled,DeleteOutlined } from '@ant-design/icons';
import CreateRelatedListDrawer from './CreateRelatedListDrawer';
import RelatedListEditDrawer from './RelatedListEditDrawer';
import ApiService from '../Components/apiService';
import { BASE_URL,DateFormat,DateTimeFormat } from '../Components/Constant';
import { useSelector } from "react-redux";
import dayjs from 'dayjs';
import {convertDateToUserTimezone} from '../Components/Utility';


const { Title } = Typography;

const ObjectRelatedListTab = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { record } = location.state || {};
  const [relatedLists, setRelatedLists] = useState([]);
  const [relatedListDrawerVisible, setRelatedListDrawerVisible] = useState(false);
  const [editRelatedListDrawerVisible, setEditRelatedListDrawerVisible] = useState(false);
  const [loadingRelatedLists, setLoadingRelatedLists] = useState(false);
  const [editingRelatedList, setEditingRelatedList] = useState(null);
  const [parentObjectName, setParentObjectName] = useState(record?.name || 'Account');
  const [pageSize, setPageSize] = useState(10);

  const security = useSelector((state) => state.data.items[0]);

  const showRelatedListDrawer = () => {
    setEditingRelatedList(null);
    if (security?.system_settings?.related_list.create === false) {
      message.error('You do not have permission to create ');
      return;
    }
    else {
      setRelatedListDrawerVisible(true);
    }
  };

  const closeRelatedListDrawer = () => {
    setRelatedListDrawerVisible(false);
    fetchRelatedLists();
  };

  const closeEditRelatedListDrawer = () => {
    setEditRelatedListDrawerVisible(false);
    fetchRelatedLists();
  };

  const handleAddRelatedList = (values) => {
    const newRelatedList = {
      name: values.relatedListName,
      related_object_name: values.relatedObjectType,
      related_field_name: values.relatedFieldType,
    };

    if (editingRelatedList) {
      setRelatedLists((prev) =>
        prev.map((list) =>
          list.key === editingRelatedList.key ? newRelatedList : list
        )
      );
    } else {
      setRelatedLists([...relatedLists, { ...newRelatedList, key: Date.now() }]);
    }
    closeRelatedListDrawer();
  };

  const fetchRelatedLists = async () => {
    setLoadingRelatedLists(true);
    try {
      const apiService = new ApiService(
        `${BASE_URL}/related_lists/for_object/${parentObjectName}`,
        { 'Content-Type': 'application/json' },
        'GET'
      );
      const response = await apiService.makeCall(navigate);

      const formattedData = response.map((item) => ({
        key: item?.related_list?._id,
        related_list_name: item?.related_list?.related_list_name,
        child_object_name: item?.related_list?.child_object_name,
        fields_to_display: item?.related_list?.fields_to_display,
        field_api_name: item?.related_list?.field_api_name,
        created_at:item?.related_list?.created_at,
        updated_at:item?.related_list?.updated_at,
        order: item?.related_list?.order
      }));

      setRelatedLists(formattedData);
    } catch (error) {
    } finally {
      setLoadingRelatedLists(false);
    }
  };

  useEffect(() => {
    if (record?.name) {
      setParentObjectName(record?.name);
      fetchRelatedLists();
    }
  }, [record]);

  const handleEditRelatedList = (record) => {
    if (security?.system_settings?.related_list.edit === false) {
      message.error('You do not have permission to edit ');
      return;
    }

    setEditingRelatedList({
      ...record,
      fields_to_display: record.fields_to_display || [],
    });

    if (security?.system_settings?.related_list.edit === false) {
      message.error('You do not have permission to edit ');
      return;
    }
    else {
      setEditRelatedListDrawerVisible(true);
    }
  };

  const deleteRecord = async (record) => {
    if (security?.system_settings?.related_list.delete === false) {
       message.error('You do not have permission to delete ');
       return;
    }

    try {
      const apiService = new ApiService(
        `${BASE_URL}/delete_related_list/${record.key}`,
        { 'Content-Type': 'application/json' },
        'DELETE'
      );
      await apiService.makeCall(navigate);
      fetchRelatedLists();
    } catch (error) { }

  }

  const relatedListColumns = [
    {
      title: 'Related List Name',
      dataIndex: 'related_list_name',
      key: 'related_list_name',
      sorter: (a, b) => a.related_list_name.localeCompare(b.related_list_name),
    },
    {
      title: 'Related Object Name',
      dataIndex: 'child_object_name',
      key: 'child_object_name',
      sorter: (a, b) => a.child_object_name.localeCompare(b.child_object_name),
    },
    {
      title: 'Related Field Name',
      dataIndex: 'fields_to_display',
      key: 'fields_to_display',
      render: (fields) => fields.join(', '), // Join for display
    }, 
    {
      title: 'Lookup Name',
      dataIndex: 'field_api_name',
      key: 'field_api_name',
      sorter: (a, b) => a.field_api_name.localeCompare(b.field_api_name),
    },
    {
      title: 'List Order',
      dataIndex: 'order',
      key: 'order',
      sorter: (a, b) => a.order - b.order,
    },
     {
          title: 'Created At',
          dataIndex: 'created_at',
          key: 'created_at',
          // width: 100,
          sorter: (a, b) => new Date(a.created_at) - new Date(b.created_at),
          render: (text) => (
            text && dayjs(text).isValid()
              ? convertDateToUserTimezone(text, DateTimeFormat)
              : ''
          ),
              
        },
        {
          title: 'Updated At',
          dataIndex: 'updated_at',
          key: 'updated_at',
          // width: 100,
          sorter: (a, b) => new Date(a.created_at) - new Date(b.created_at),
          render: (text) => (
            text && dayjs(text).isValid()
              ? convertDateToUserTimezone(text, DateTimeFormat)
              : ''
          ),
              
        },
    {
      title: 'Actions',
      key: 'actions',
      render: (text, record) => (
        <>
          <Space size="middle">
            {<Tooltip title="Edit">
              <EditOutlined
                onClick={() => handleEditRelatedList(record)}
                style={{ marginRight: 5, fontSize: '12px', cursor: 'pointer' }}
              />
            </Tooltip>}
          </Space>

          <Tooltip title="Delete">
            <Popconfirm
              title="Are you sure you want to delete this item?"
              onConfirm={() => deleteRecord(record)}
              okText="Yes"
              cancelText="No"
            >
              <DeleteOutlined style={{ color: 'red', marginRight: 5, fontSize: '12px', cursor: 'pointer' }} />
            </Popconfirm>
          </Tooltip>
        </>
      ),
    },
  ];

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 10 }}>
        <Col>
          <Title level={4}>Related Lists</Title>
        </Col>
        <Col >
          <Button type="primary" onClick={showRelatedListDrawer} style={{ marginBottom: 5 }}>
            Create Related List
          </Button>
        </Col>
      </Row>
      <Spin spinning={loadingRelatedLists}>
        <Table columns={relatedListColumns} dataSource={relatedLists} scroll={{ y: 550 }}
              pagination={{
              pageSize: pageSize,
              showSizeChanger: true,
              pageSizeOptions: ['10', '20', '50', '100'],
              onChange: (page, pageSize) => {
                setPageSize(pageSize);
              },              
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
            }} />
      </Spin>
      <CreateRelatedListDrawer
        visible={relatedListDrawerVisible}
        onClose={closeRelatedListDrawer}
        onAddRelatedList={handleAddRelatedList}
        parentObjectName={parentObjectName}
        editingRelatedList={editingRelatedList}
      />
      <RelatedListEditDrawer
        visible={editRelatedListDrawerVisible}
        onClose={closeEditRelatedListDrawer}
        record={editingRelatedList}
        parentObjectName={parentObjectName}
      />
    </div>
  );
};

export default ObjectRelatedListTab;
